import React, { forwardRef } from "react";
import { X } from "@semo/icons";
import type { ChatHeaderProps } from "../types";
import { PartyAvatar } from "../atoms/PartyAvatar";
import { VendorBadge, Button } from "@semo/shared-ui"; // VendorBadge is in shared-ui items
import { cn } from "@semo/shared-ui";

/**
 * ChatHeader - 채팅방 헤더 컴포넌트
 *
 * 상대방 정보, 온라인 상태, 신뢰도를 표시합니다.
 * buyer/vendor 모두 동일한 UI를 사용합니다.
 *
 * Presence 상태는 외부에서 주입받습니다.
 */
const ChatHeaderComponent = forwardRef<HTMLDivElement, ChatHeaderProps>(
  (
    {
      otherParty,
      role: _role,
      onClose,
      isActive = false,
      statusText,
      hideClose = false,
    },
    ref,
  ) => {
    const { name, imageUrl, trustPercentile } = otherParty;

    return (
      <div
        ref={ref}
        data-slot="chat-header"
        className={cn(
          "h-[60px] sm:h-[70px] px-3 sm:px-6",
          "border-b border-slate-100 ",
          "flex items-center justify-between lg:hidden",
          "bg-white  shrink-0",
        )}
      >
        {/* Left: Party Info */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          {/* Avatar with Online Indicator */}
          <div className="relative shrink-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-slate-100  overflow-hidden">
              <PartyAvatar
                name={name}
                imageUrl={imageUrl}
                className="w-full h-full"
                size="lg"
              />
            </div>
            {/* Online Status Indicator */}
            <div
              className={cn(
                "absolute bottom-0 right-0",
                "w-2.5 h-2.5 sm:w-3 sm:h-3",
                "border-2 border-white  rounded-full",
                isActive ? "bg-[#16a34a] animate-pulse" : "bg-slate-400",
              )}
            />
          </div>

          {/* Name and Status */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <h2 className="text-sm sm:text-base font-semibold text-slate-900  truncate">
                {name}
              </h2>
              {/* Reliability Range Badge (Render only if trustPercentile exists) */}
              {trustPercentile && (
                <VendorBadge
                  variant="trustPercentile"
                  trustPercentile={trustPercentile}
                />
              )}
            </div>
            {/* Status Text */}
            {statusText && (
              <span
                className={cn(
                  "hidden sm:flex text-xs items-center gap-1",
                  isActive ? "text-green-600 " : "text-slate-500 ",
                )}
              >
                {statusText}
              </span>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {!hideClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 rounded-lg"
              aria-label="채팅방 닫기"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </Button>
          )}
        </div>
      </div>
    );
  },
);

ChatHeaderComponent.displayName = "ChatHeader";

/**
 * ✅ React.memo wrapper to prevent unnecessary re-renders
 *
 * All props should be stable from parent's useCallback/useMemo
 */
export const ChatHeader = React.memo(ChatHeaderComponent);
